import moment from 'moment/moment'

import agent from 'agent'
import {
  ProvenanceTargetType,
  TargetType
} from 'components/attacks/create/create-attack-types'
import {
  buildLabelSelectionObject,
  type KubernetesTargetFormValues,
  K8sTargetingSelectionType
} from 'components/attacks/create/targets/kubernetes/kubernetes-attack-form-utils'
import { getTargetParamsKey } from 'components/attacks/create/targets/target-utils'
import toast from 'lib/toast'
import {
  InfraStrategyType,
  type AttackConfiguration,
  type AttackSamplingConfiguration,
  type AttackTargetingConfiguration,
  type ContainerTargetingStrategy,
  type HostTargetingStrategy,
  type KubernetesTargetingStrategy,
  type Names,
  type SnakeCaseAttackCommandConfiguration,
  type TargetingStrategy
} from 'types/api/attacks/details/targeting'
import { ProcessProvenance } from 'types/api/services/service'
import { naturalSortBy, unique } from 'utils/list'
import { cliArgsToInfraCommandArgs } from 'utils/metadata'
import { messageFromResponseError } from 'utils/strings'

import type {
  InfraImpactFormValues,
  InfraTargetFormValues,
  InfraTriggerFormValues
} from 'components/attacks/create/utils/infra-initial-form-state'
import type { NavigateFunction } from 'react-router-dom'
import type { Client } from 'types/api/attacks/client'
import type { K8sObject } from 'types/api/kubernetes/kubernetes'
import type { AttackTrigger } from 'types/api/schedules/active'
import type { InfraTargetingAutoCompleteSearchOption } from 'types/forms'
import type { CommandArg, Metadata } from 'types/metadata'

export function getProvenanceFromAttackTargetType(
  targetType: TargetType
): ProcessProvenance {
  switch (targetType) {
    case TargetType.Containers:
      return ProcessProvenance.Container
    default:
      return ProcessProvenance.ProcessData
  }
}

export function createTargetingStrategyFromInfraForm(
  processProvenance: ProcessProvenance,
  target: InfraTargetFormValues,
  targetSelectionStrategy: string,
  targetType: TargetType,
  selectedTargets: string[]
): TargetingStrategy {
  const targetingStrategy: TargetingStrategy = {
    type: ProvenanceTargetType[processProvenance],
    isAll: Boolean(target.targetAll)
  }

  if (!target.targetAll) {
    if (targetSelectionStrategy === 'Random') {
      targetingStrategy[
        getTargetParamsKey(targetType, targetSelectionStrategy)
      ] = target.targetParams
    } else {
      targetingStrategy.ids = selectedTargets
    }
  }

  return targetingStrategy
}

export function getUniqueDeviceListFromClients(clients: Client[]) {
  return naturalSortBy(
    unique(
      clients
        .map((client) => client.network_interface_data?.interfaces)
        .filter((list) => list?.length > 0)
        .flatMap((interfaces) => {
          return interfaces.map((iface) => iface.name)
        })
    )
  )
}

export function generateAttackConfigurationSampling(
  target: InfraTargetFormValues | KubernetesTargetFormValues
): AttackSamplingConfiguration {
  if (target.exact) {
    return {
      type: 'Exact',
      count: target.exact
    }
  } else {
    return {
      type: 'Even',
      percent: target.percentage || 100
    }
  }
}

function generateHostOrContainerAttackConfigurationTargets(
  target: InfraTargetFormValues,
  type: ProvenanceTargetType.ProcessData | ProvenanceTargetType.Container
): AttackTargetingConfiguration {
  const { selectedTargets, targetAll, targetParams, targetingStrategyType } =
    target

  const targetingStrategy: HostTargetingStrategy | ContainerTargetingStrategy =
    { type }

  if (targetingStrategyType === InfraStrategyType.Exact) {
    targetingStrategy.ids = [...selectedTargets]
  } else if (targetAll) {
    targetingStrategy.isAll = targetAll
  } else {
    targetingStrategy[getTargetParamsKey(type, targetingStrategyType)] =
      formatTargetParams(targetParams)
  }

  return [{ targetingStrategy }]
}

function generateKubernetesAttackConfigurationTargets(
  target: KubernetesTargetFormValues,
  impact: InfraImpactFormValues,
  targets: K8sObject[]
): AttackTargetingConfiguration {
  const targetingStrategy: KubernetesTargetingStrategy = {
    type: ProvenanceTargetType.Kubernetes,
    isAll: false
  }

  if (impact.containerSelection?.selectionType) {
    targetingStrategy.containerSelection = {
      selectionType: impact.containerSelection.selectionType.toUpperCase(),
      containerNames: [...impact.containerSelection.containerNames]
    }
  }

  if (target.targetingSelectionType === K8sTargetingSelectionType.Selectors) {
    targetingStrategy.labelSelection = buildLabelSelectionObject(
      target.labelSelector,
      target.selectedClusterId,
      target.selectedNamespace
    )
  } else {
    const targetNames: Names[] = targets
      ?.filter((currentTarget) => target.selectedObjects.has(currentTarget.uid))
      .map(({ clusterId, namespace, kind, name }) => ({
        clusterId,
        kind,
        namespace,
        name
      }))

    targetingStrategy.names = targetNames
  }

  return [{ targetingStrategy }]
}

export function generateAttackConfigurationTargets(
  targetType: TargetType,
  target: InfraTargetFormValues | KubernetesTargetFormValues,
  impact: InfraImpactFormValues,
  targets: string[] | K8sObject[]
) {
  if (targetType === TargetType.Hosts) {
    return generateHostOrContainerAttackConfigurationTargets(
      target as InfraTargetFormValues,
      ProvenanceTargetType.ProcessData
    )
  }

  if (targetType === TargetType.Containers) {
    return generateHostOrContainerAttackConfigurationTargets(
      target as InfraTargetFormValues,
      ProvenanceTargetType.Container
    )
  }

  // Return Kubernetes targets by default
  return generateKubernetesAttackConfigurationTargets(
    target as KubernetesTargetFormValues,
    impact,
    targets
  )
}

export function generateAttackConfigurationCommand(
  impact: InfraImpactFormValues,
  metadata: Metadata
) {
  const metadataForCommand = metadata?.find(
    ({ command: commandType }) => commandType === impact.type
  )

  const infraCommandArgs = cliArgsToInfraCommandArgs({
    command: impact.type,
    args: impact.args,
    attackCategory: impact.category,
    providers: impact.providers,
    trafficImpactMapping: impact.traffic_impact_mapping,
    metadata
  })

  const command: SnakeCaseAttackCommandConfiguration = {
    infraCommandType: impact.type,
    infraCommandArgs: {
      ...infraCommandArgs,
      cliArgs: [
        impact.type,
        ...(impact?.args?.map((arg) => arg.toString()) ?? [])
      ]
    }
  }

  if (metadataForCommand?.type === 'Network') {
    command.infraCommandArgs.providers = impact.providers
    if (impact.traffic_impact_mapping) {
      command.infraCommandArgs.traffic_impact_mapping =
        impact.traffic_impact_mapping
    }
  }

  return command
}

export function generateAttackConfiguration(
  targetType: TargetType,
  {
    target,
    impact
  }: {
    target: InfraTargetFormValues | KubernetesTargetFormValues
    impact: InfraImpactFormValues
  },
  metadata: Metadata,
  targets: string[] | K8sObject[]
): AttackConfiguration {
  return {
    command: generateAttackConfigurationCommand(impact, metadata),
    sampling: generateAttackConfigurationSampling(target),
    targets: generateAttackConfigurationTargets(
      targetType,
      target,
      impact,
      targets
    )
  }
}

export function generatesServiceAttackConfiguration(
  targetType: ProvenanceTargetType,
  {
    target,
    impact
  }: {
    target: InfraTargetFormValues | KubernetesTargetFormValues
    impact: InfraImpactFormValues
  },
  metadata: Metadata,
  serviceId: string
): AttackConfiguration {
  return {
    command: generateAttackConfigurationCommand(impact, metadata),
    sampling: generateAttackConfigurationSampling(target),
    targets: [
      {
        serviceId,
        targetingStrategy: {
          type: targetType,
          isAll: true
        }
      }
    ]
  }
}

/**
 * Get the list of OSes that are not supported by the selected argument
 */
export function getUnsupportedOSListForArgument(
  selectedArgument?: CommandArg,
  clientsOSList?: string[]
) {
  if (selectedArgument && clientsOSList) {
    return clientsOSList.filter(
      (v) => !Object.keys(selectedArgument.versions).includes(v)
    )
  }

  return []
}

export function getNextTargetParams(
  newValue:
    | InfraTargetingAutoCompleteSearchOption
    | InfraTargetingAutoCompleteSearchOption[]
) {
  const nextTargetParams = {}

  if (Array.isArray(newValue)) {
    newValue.forEach((val) => {
      const { paramKey, paramValue } = val.value

      nextTargetParams[paramKey] =
        nextTargetParams[paramKey] || new Set<string>()
      nextTargetParams[paramKey].add(paramValue)
    })
  } else {
    const { paramKey, paramValue } = newValue.value

    nextTargetParams[paramKey] = nextTargetParams[paramKey] || new Set<string>()
    nextTargetParams[paramKey].add(paramValue)
  }

  return nextTargetParams
}

// InfraAttackForm
export function formatTargetParams(
  targetParams: Record<string, Set<string>>
): Record<string, string[]> {
  return Object.entries(targetParams).reduce((params, [key, values]) => {
    // Value is empty, skip
    if (values.size === 0) {
      return params
    }

    // Converting from Set() to array for API endpoint
    params[key] = Array.from(values)

    return params
  }, {})
}

export type CreateInfraAttackRequestBody = {
  continueOnEmptyTargets?: boolean
  includeNewTargets?: boolean
} & ( // '/attacks` expects attackConfiguration
  | {
      attackConfiguration: AttackConfiguration
      attack_configuration?: never
      trigger?: never
    }
  // `schedules/attacks` expects attack_configuration
  | {
      attackConfiguration?: never
      attack_configuration: AttackConfiguration
      trigger: AttackTrigger
    }
)

export function createAttackRequestBody(
  targetType: TargetType,
  {
    target,
    impact,
    trigger
  }: {
    target: InfraTargetFormValues
    impact: InfraImpactFormValues
    trigger: InfraTriggerFormValues
  },
  metadata: Metadata,
  service?: { serviceId: string; targetType: ProvenanceTargetType }
): CreateInfraAttackRequestBody | undefined {
  const { selectedTargets } = target

  if (metadata.length === 0) {
    return
  }

  const attackConfiguration = service
    ? generatesServiceAttackConfiguration(
        service.targetType,
        { impact, target },
        metadata,
        service.serviceId
      )
    : generateAttackConfiguration(
        targetType,
        { impact, target },
        metadata,
        selectedTargets
      )

  const baseRequestBody = {
    ...('includeNewTargets' in target && {
      includeNewTargets: Boolean(target.includeNewTargets)
    }),
    ...('continueOnEmptyTargets' in impact && {
      continueOnEmptyTargets: Boolean(impact.continueOnEmptyTargets)
    })
  }

  const triggerProps =
    trigger.type === 'Once'
      ? {
          type: trigger.type,
          exact: moment(
            `${moment(trigger.exactDate).format('MM/DD/YYYY')} ${
              trigger.exactTime
            }`
          ).toISOString()
        }
      : trigger

  return trigger.enabled
    ? {
        attack_configuration: attackConfiguration,
        trigger: triggerProps,
        ...baseRequestBody
      }
    : {
        attackConfiguration,
        ...baseRequestBody
      }
}

export async function createInfraAttack(
  targetType: TargetType,
  {
    target,
    impact,
    trigger
  }: {
    target: InfraTargetFormValues
    impact: InfraImpactFormValues
    trigger: InfraTriggerFormValues
  },
  metadata: Metadata,
  currentTeamId: string,
  navigate: NavigateFunction
) {
  const body = createAttackRequestBody(
    targetType,
    { target, impact, trigger },
    metadata
  )

  const hasTrigger = trigger.enabled

  const apiUrl = hasTrigger ? 'schedules/attacks' : 'attacks'

  try {
    const res = await agent.post(`/${apiUrl}?teamId=${currentTeamId}`, body)

    if (!res?.data) {
      toast({
        type: 'error',
        text: 'Error creating experiment'
      })
    }

    const newAttack: { attackId: string } = hasTrigger ? res.data : res.data[0]

    navigate(trigger.enabled ? '/schedules' : `/attacks/${newAttack.attackId}`)

    if (trigger.enabled) {
      toast({
        text: 'Preparing your scheduled experiment. It will be displayed in the Experiments section momentarily when it is scheduled.',
        time: 15_000
      })
    }
  } catch (err) {
    toast({
      type: 'error',
      text: messageFromResponseError(
        err,
        'There was an issue running this experiment. Please try again later.'
      )
    })
    // throw so that we can avoid resetting the form on failure (caught in onSubmit in InfraAttackForm)
    throw err
  }
}
